import { useCallback, useState, type RefObject } from "react";

interface HoveredLink {
  url: string;
  top: number;
  left: number;
}

interface UseEditorLinkHoverParams {
  editorRef: RefObject<HTMLDivElement | null>;
  containerRef: RefObject<HTMLDivElement | null>;
}

export const useEditorLinkHover = ({
  editorRef,
  containerRef,
}: UseEditorLinkHoverParams) => {
  const [hoveredLink, setHoveredLink] = useState<HoveredLink | null>(null);

  const handlePointerOver = useCallback(
    (e: React.PointerEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (
        !anchor ||
        !editorRef.current?.contains(anchor) ||
        !containerRef.current
      ) {
        return;
      }

      const href = anchor.getAttribute("href");
      if (!href) {
        return;
      }

      const linkRect = anchor.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();

      setHoveredLink({
        url: href,
        top:
          linkRect.bottom -
          containerRect.top +
          containerRef.current.scrollTop +
          5,
        left:
          linkRect.left - containerRect.left + containerRef.current.scrollLeft,
      });
    },
    [containerRef, editorRef],
  );

  const handlePointerOut = useCallback((e: React.PointerEvent) => {
    const target = e.target as HTMLElement;
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    const fromAnchor = target.closest("a[href]");
    const toAnchor = relatedTarget?.closest("a[href]");
    if (fromAnchor && !toAnchor) {
      setHoveredLink(null);
    }
  }, []);

  return {
    hoveredLink,
    handlePointerOver,
    handlePointerOut,
  };
};
